/** VISTOERA_CANVAS_CANONICAL_HOVER_QUICK_ACTION_ADMIN_DRIVEN_NEXT_NODE_
 * CONTEXT_HANDOFF_P0_V1 — pure Quick Action resolution logic.
 *
 * Deliberately has ZERO runtime dependency on `../../../api` (or
 * anything that transitively imports it) — every other Canvas runtime
 * cache (`nodeCatalogRuntime.ts`, `canvasQuickActionRuntime.ts`) needs
 * that import for its real network fetch, which makes them unsafe to
 * import from a plain `tsx` node-test (`import.meta.env` only exists
 * under Vite's own transform). Keeping the actual grouping/filtering
 * math here — with plain data in, plain data out — is what lets Gate M
 * (T1/T5/T8/T9) assert real behavior without a React renderer or a
 * running backend. `canvasQuickActionRuntime.ts` and
 * `CanvasResultQuickActionRail.tsx` both delegate to this module rather
 * than re-implementing the same logic twice. */

export interface QuickActionRow {
  action_key: string
  source_node_type: string
  source_output_type: string
  target_node_type: string
  target_capability: string | null
  label: string
  input_binding_strategy: string
  enabled_by_default: boolean
  enabled: boolean
  sort_order: number
}

/** Group a flat list into `source_node_type -> sort_order-ascending
 * rows`. The ONE place both the real fetch path and any synthetic test
 * snapshot funnel through so grouping/sort order can never drift. */
export function groupQuickActionsBySource(actions: QuickActionRow[]): Map<string, QuickActionRow[]> {
  const next = new Map<string, QuickActionRow[]>()
  for (const a of actions) {
    const list = next.get(a.source_node_type) ?? []
    list.push(a)
    next.set(a.source_node_type, list)
  }
  for (const list of next.values()) {
    list.sort((x, y) => x.sort_order - y.sort_order)
  }
  return next
}

/** Execution guard (TASK-00584): is `source -> target` currently an
 * Admin-enabled action in the given config snapshot? Fail-closed — an
 * empty/not-yet-loaded snapshot never authorises execution. Used by the
 * rail click handler and `CanvasBoard.onQuickGenerate` so a disabled
 * action cannot run through stale component state / cached handlers. */
export function isQuickActionEnabledInRows(
  rowsForSource: QuickActionRow[],
  targetNodeType: string,
): boolean {
  return rowsForSource.some((r) => r.target_node_type === targetNodeType && r.enabled !== false)
}

export interface QuickActionHandlerLike {
  targetNodeType: string
}

/** Rows for ONE source node type (already Admin-enabled + sort_order
 * ordered, e.g. from `getEnabledQuickActionsForSource`), intersected
 * with the handlers THIS node actually implements (fail-closed — an
 * Admin-enabled action with no matching handler renders nothing rather
 * than a dead button) and the tenant catalog's live creatability for
 * the target node type. */
export function resolveVisibleQuickActionRows<H extends QuickActionHandlerLike>(
  rowsForSource: QuickActionRow[],
  actionHandlers: H[],
  isTargetCreatable: (targetNodeType: string) => boolean,
): QuickActionRow[] {
  const handledTargets = new Set(actionHandlers.map((h) => h.targetNodeType))
  return rowsForSource.filter((row) => handledTargets.has(row.target_node_type) && isTargetCreatable(row.target_node_type))
}
